import { Alert, Button, Card, CardBody, Input, Select, Spinner } from '@foundation/ui'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import styles from './widgets.$id.edit.module.css'
import { api, type Widget, type WidgetLayout } from '@/lib/api'
import { toast } from '@/lib/toast'

export const Route = createFileRoute('/widgets/$id/edit')({
  component: WidgetEdit,
})

const LAYOUT_OPTIONS: { value: WidgetLayout; label: string; icon: string }[] = [
  { value: 'grid', label: 'Wall of Love', icon: '\uD83E\uDDF1' },
  { value: 'carousel', label: 'Carousel', icon: '\uD83C\uDFA0' },
  { value: 'list', label: 'List', icon: '\uD83D\uDCCB' },
  { value: 'masonry', label: 'Masonry', icon: '\uD83E\uDDE9' },
  { value: 'marquee', label: 'Marquee', icon: '\uD83C\uDFAC' },
  { value: 'badge', label: 'Badge', icon: '\uD83C\uDFC5' },
]

const DISPLAY_ORDER_OPTIONS = [
  { value: 'newest', label: 'Newest First' },
  { value: 'oldest', label: 'Oldest First' },
  { value: 'highest_rated', label: 'Highest Rated' },
  { value: 'random', label: 'Random' },
]

const CARD_STYLE_OPTIONS = [
  { value: 'shadow', label: 'Shadow' },
  { value: 'bordered', label: 'Bordered' },
  { value: 'minimal', label: 'Minimal' },
]

const FONT_OPTIONS = [
  { value: 'system', label: 'System Default' },
  { value: 'inter', label: 'Inter' },
  { value: 'roboto', label: 'Roboto' },
  { value: 'open-sans', label: 'Open Sans' },
  { value: 'lato', label: 'Lato' },
]

function WidgetEdit() {
  const { id } = Route.useParams()
  const navigate = useNavigate()

  const [widget, setWidget] = useState<Widget | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [formData, setFormData] = useState({
    name: '',
    layout: 'grid' as WidgetLayout,
    maxTestimonials: 10,
    displayOrder: 'newest',
    backgroundColor: '#ffffff',
    textColor: '#1a1a1a',
    accentColor: '#6366f1',
    borderRadius: 8,
    fontFamily: 'system',
    cardStyle: 'shadow',
    showRatings: true,
    showPhotos: true,
    showCompanyRole: true,
    showDate: true,
  })

  useEffect(() => {
    const fetchWidget = async () => {
      try {
        setIsLoading(true)
        setError(null)
        const response = await api.getWidget(id)
        const w = response.data
        setWidget(w)
        setFormData({
          name: w.name,
          layout: w.layout,
          maxTestimonials: w.maxTestimonials,
          displayOrder: w.displayOrder,
          backgroundColor: w.backgroundColor,
          textColor: w.textColor,
          accentColor: w.accentColor,
          borderRadius: w.borderRadius,
          fontFamily: w.fontFamily,
          cardStyle: w.cardStyle,
          showRatings: w.showRatings,
          showPhotos: w.showPhotos,
          showCompanyRole: w.showCompanyRole,
          showDate: w.showDate,
        })
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load widget')
      } finally {
        setIsLoading(false)
      }
    }
    void fetchWidget()
  }, [id])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!formData.name.trim()) {
      setError('Widget name is required')
      return
    }

    try {
      setIsSubmitting(true)
      const response = await api.updateWidget(id, {
        name: formData.name.trim(),
        layout: formData.layout,
        maxTestimonials: formData.maxTestimonials,
        displayOrder: formData.displayOrder,
        backgroundColor: formData.backgroundColor,
        textColor: formData.textColor,
        accentColor: formData.accentColor,
        borderRadius: formData.borderRadius,
        fontFamily: formData.fontFamily,
        cardStyle: formData.cardStyle,
        showRatings: formData.showRatings,
        showPhotos: formData.showPhotos,
        showCompanyRole: formData.showCompanyRole,
        showDate: formData.showDate,
      })
      setWidget(response.data)
      toast.success('Widget updated successfully')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update widget')
    } finally {
      setIsSubmitting(false)
    }
  }

  const layoutLabel =
    LAYOUT_OPTIONS.find((l) => l.value === formData.layout)?.label ?? formData.layout

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="lg" />
        <p>Loading widget...</p>
      </div>
    )
  }

  if (error && !widget) {
    return <Alert variant="danger">{error}</Alert>
  }

  return (
    <>
      {error && (
        <Alert variant="danger" style={{ marginBottom: '1rem' }}>
          {error}
        </Alert>
      )}

      <div className={styles.builder}>
        {/* Left: Configuration */}
        <div className={styles.configPanel}>
          {/* Layout Selection */}
          <Card>
            <CardBody>
              <h3 className={styles.sectionTitle}>Choose Layout</h3>
              <div className={styles.layoutGrid}>
                {LAYOUT_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={`${styles.layoutOption} ${formData.layout === option.value ? styles.layoutOptionActive : ''}`}
                    onClick={() => setFormData({ ...formData, layout: option.value })}
                  >
                    <span className={styles.layoutIcon}>{option.icon}</span>
                    <span className={styles.layoutLabel}>{option.label}</span>
                  </button>
                ))}
              </div>
            </CardBody>
          </Card>

          {/* Widget Configuration */}
          <Card>
            <CardBody>
              <h3 className={styles.sectionTitle}>Configuration</h3>
              <form className={styles.form} onSubmit={(e) => void handleSubmit(e)}>
                <Input
                  label="Widget Name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Homepage Testimonials"
                  required
                />

                <Input
                  label="Max Testimonials"
                  type="number"
                  value={formData.maxTestimonials.toString()}
                  onChange={(e) =>
                    setFormData({ ...formData, maxTestimonials: parseInt(e.target.value) || 1 })
                  }
                  min={1}
                  max={100}
                />
                <p className={styles.fieldHint}>Maximum number of testimonials to display.</p>

                <Select
                  label="Display Order"
                  options={DISPLAY_ORDER_OPTIONS}
                  value={formData.displayOrder}
                  onChange={(e) => setFormData({ ...formData, displayOrder: e.target.value })}
                />

                <Select
                  label="Card Style"
                  options={CARD_STYLE_OPTIONS}
                  value={formData.cardStyle}
                  onChange={(e) => setFormData({ ...formData, cardStyle: e.target.value })}
                />

                <Select
                  label="Font Family"
                  options={FONT_OPTIONS}
                  value={formData.fontFamily}
                  onChange={(e) => setFormData({ ...formData, fontFamily: e.target.value })}
                />

                <Input
                  label="Border Radius"
                  type="number"
                  value={formData.borderRadius.toString()}
                  onChange={(e) =>
                    setFormData({ ...formData, borderRadius: parseInt(e.target.value) || 0 })
                  }
                  min={0}
                  max={24}
                />
                <p className={styles.fieldHint}>Corner roundness in pixels.</p>

                {/* Colors */}
                <h4 className={styles.sectionTitle}>Colors</h4>
                <div className={styles.colorFields}>
                  <div className={styles.colorField}>
                    <span className={styles.colorFieldLabel}>Background</span>
                    <div className={styles.colorFieldInput}>
                      <input
                        type="color"
                        className={styles.colorSwatch}
                        value={formData.backgroundColor}
                        onChange={(e) =>
                          setFormData({ ...formData, backgroundColor: e.target.value })
                        }
                      />
                      <span className={styles.colorValue}>{formData.backgroundColor}</span>
                    </div>
                  </div>
                  <div className={styles.colorField}>
                    <span className={styles.colorFieldLabel}>Text</span>
                    <div className={styles.colorFieldInput}>
                      <input
                        type="color"
                        className={styles.colorSwatch}
                        value={formData.textColor}
                        onChange={(e) => setFormData({ ...formData, textColor: e.target.value })}
                      />
                      <span className={styles.colorValue}>{formData.textColor}</span>
                    </div>
                  </div>
                  <div className={styles.colorField}>
                    <span className={styles.colorFieldLabel}>Accent</span>
                    <div className={styles.colorFieldInput}>
                      <input
                        type="color"
                        className={styles.colorSwatch}
                        value={formData.accentColor}
                        onChange={(e) => setFormData({ ...formData, accentColor: e.target.value })}
                      />
                      <span className={styles.colorValue}>{formData.accentColor}</span>
                    </div>
                  </div>
                </div>

                {/* Display Toggles */}
                <h4 className={styles.sectionTitle}>Display Options</h4>
                <div className={styles.toggleGroup}>
                  <div className={styles.toggle}>
                    <label className={styles.toggleLabel}>
                      <input
                        type="checkbox"
                        checked={formData.showRatings}
                        onChange={(e) =>
                          setFormData({ ...formData, showRatings: e.target.checked })
                        }
                      />
                      <span className={styles.toggleSwitch} />
                      <span className={styles.toggleText}>Show star ratings</span>
                    </label>
                  </div>
                  <div className={styles.toggle}>
                    <label className={styles.toggleLabel}>
                      <input
                        type="checkbox"
                        checked={formData.showPhotos}
                        onChange={(e) => setFormData({ ...formData, showPhotos: e.target.checked })}
                      />
                      <span className={styles.toggleSwitch} />
                      <span className={styles.toggleText}>Show author photos</span>
                    </label>
                  </div>
                  <div className={styles.toggle}>
                    <label className={styles.toggleLabel}>
                      <input
                        type="checkbox"
                        checked={formData.showCompanyRole}
                        onChange={(e) =>
                          setFormData({ ...formData, showCompanyRole: e.target.checked })
                        }
                      />
                      <span className={styles.toggleSwitch} />
                      <span className={styles.toggleText}>Show company &amp; role</span>
                    </label>
                  </div>
                  <div className={styles.toggle}>
                    <label className={styles.toggleLabel}>
                      <input
                        type="checkbox"
                        checked={formData.showDate}
                        onChange={(e) => setFormData({ ...formData, showDate: e.target.checked })}
                      />
                      <span className={styles.toggleSwitch} />
                      <span className={styles.toggleText}>Show submission date</span>
                    </label>
                  </div>
                </div>

                <div className={styles.formActions}>
                  <Button type="submit" variant="primary" loading={isSubmitting}>
                    Save Changes
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => void navigate({ to: '/widgets/$id/embed', params: { id } })}
                  >
                    View Embed Code
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => void navigate({ to: '/widgets' })}
                  >
                    Back to List
                  </Button>
                </div>
              </form>
            </CardBody>
          </Card>
        </div>

        {/* Right: Preview */}
        <div className={styles.previewPanel}>
          <Card>
            <CardBody>
              <h3 className={styles.previewTitle}>Preview</h3>
              <div
                className={styles.previewBox}
                style={{
                  backgroundColor: formData.backgroundColor,
                  borderRadius: `${formData.borderRadius}px`,
                }}
              >
                <div className={styles.previewConfig}>
                  <div className={styles.previewRow}>
                    <span className={styles.previewLabel}>Layout</span>
                    <span className={styles.previewValue}>{layoutLabel}</span>
                  </div>
                  <div className={styles.previewRow}>
                    <span className={styles.previewLabel}>Name</span>
                    <span className={styles.previewValue}>{formData.name || '(untitled)'}</span>
                  </div>
                  <div className={styles.previewRow}>
                    <span className={styles.previewLabel}>Max Items</span>
                    <span className={styles.previewValue}>{formData.maxTestimonials}</span>
                  </div>
                  <div className={styles.previewRow}>
                    <span className={styles.previewLabel}>Order</span>
                    <span className={styles.previewValue}>
                      {DISPLAY_ORDER_OPTIONS.find((o) => o.value === formData.displayOrder)?.label}
                    </span>
                  </div>
                  <div className={styles.previewRow}>
                    <span className={styles.previewLabel}>Card Style</span>
                    <span className={styles.previewValue}>
                      {CARD_STYLE_OPTIONS.find((o) => o.value === formData.cardStyle)?.label}
                    </span>
                  </div>
                  <div className={styles.previewRow}>
                    <span className={styles.previewLabel}>Background</span>
                    <span className={styles.previewValue}>
                      <span
                        className={styles.previewColorSwatch}
                        style={{ backgroundColor: formData.backgroundColor }}
                      />
                      {formData.backgroundColor}
                    </span>
                  </div>
                  <div className={styles.previewRow}>
                    <span className={styles.previewLabel}>Text</span>
                    <span className={styles.previewValue}>
                      <span
                        className={styles.previewColorSwatch}
                        style={{ backgroundColor: formData.textColor }}
                      />
                      {formData.textColor}
                    </span>
                  </div>
                  <div className={styles.previewRow}>
                    <span className={styles.previewLabel}>Accent</span>
                    <span className={styles.previewValue}>
                      <span
                        className={styles.previewColorSwatch}
                        style={{ backgroundColor: formData.accentColor }}
                      />
                      {formData.accentColor}
                    </span>
                  </div>
                  <div className={styles.previewRow}>
                    <span className={styles.previewLabel}>Ratings</span>
                    <span className={styles.previewValue}>
                      {formData.showRatings ? 'Visible' : 'Hidden'}
                    </span>
                  </div>
                  <div className={styles.previewRow}>
                    <span className={styles.previewLabel}>Photos</span>
                    <span className={styles.previewValue}>
                      {formData.showPhotos ? 'Visible' : 'Hidden'}
                    </span>
                  </div>
                  <div className={styles.previewRow}>
                    <span className={styles.previewLabel}>Company/Role</span>
                    <span className={styles.previewValue}>
                      {formData.showCompanyRole ? 'Visible' : 'Hidden'}
                    </span>
                  </div>
                  <div className={styles.previewRow}>
                    <span className={styles.previewLabel}>Date</span>
                    <span className={styles.previewValue}>
                      {formData.showDate ? 'Visible' : 'Hidden'}
                    </span>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    </>
  )
}
